import { useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const ExpenseIncomeChart = ({ allMonthlyData, allYearlyData, currMonthlyData, currYearlyData, property, propertyType, setPropertyType }) => {
    const [timeRange, setTimeRange] = useState('monthly');

    const getData = () => {
        if (propertyType === "All Property") {
            return timeRange === 'monthly' ? allMonthlyData : allYearlyData;
        } else {
            return timeRange === 'monthly' ? currMonthlyData : currYearlyData;
        }
    };

    const currentData = getData();

    const sortedMonthlyData = useMemo(() => {
        if (!currentData?.graphData || timeRange === 'yearly') return [];
        return [...currentData.graphData].sort((a, b) => {
            if (a.year !== b.year) {
                return a.year - b.year;
            }
            return a.month - b.month;
        });
    }, [currentData, timeRange]);

    const yearlyData = useMemo(() => {
        if (!currentData?.graphData || timeRange === 'monthly') return [];
        const yearMap = {};

        currentData.graphData.forEach(item => {
            if (!yearMap[item.year]) {
                yearMap[item.year] = {
                    year: item.year,
                    income: 0,
                    expense: 0
                };
            }
            yearMap[item.year].income += item.income || 0;
            yearMap[item.year].expense += item.expense || 0;
        });
        return Object.values(yearMap).sort((a, b) => a.year - b.year);
    }, [currentData, timeRange]);

    const getMonthYearLabel = (month, year) => {
        const date = new Date(year, month - 1, 1);
        return date.toLocaleString('default', {
            month: 'short',
            year: '2-digit',
        });
    };

    const chartData = useMemo(() => {
        if (timeRange === 'monthly') {
            return {
                labels: sortedMonthlyData.map(item =>
                    getMonthYearLabel(item.month, item.year)
                ),
                datasets: [
                    {
                        label: 'Collection',
                        data: sortedMonthlyData.map(item => item.income || 0),
                        borderColor: '#3B82F6',
                        backgroundColor: 'rgba(59, 130, 246, 0.08)', // blue
                        tension: 0.4,
                        fill: true,
                    },
                    {
                        label: 'Expense',
                        data: sortedMonthlyData.map(item => item.expense || 0),
                        borderColor: '#F56565',
                        backgroundColor: 'rgba(239, 68, 68, 0.08)', // red
                        tension: 0.4,
                        fill: true,
                    }
                ]
            };
        } else {
            return {
                labels: yearlyData.map(item => item.year.toString()),
                datasets: [
                    {
                        label: 'Collection',
                        data: yearlyData.map(item => item.income),
                        borderColor: '#3B82F6',
                        backgroundColor: 'rgba(59, 130, 246, 0.08)', // blue
                        tension: 0.4,
                        fill: true,
                    },
                    {
                        label: 'Expense',
                        data: yearlyData.map(item => item.expense),
                        borderColor: '#F56565',
                        backgroundColor: 'rgba(239, 68, 68, 0.08)', // red
                        tension: 0.4,
                        fill: true,
                    }
                ]
            };
        }
    }, [sortedMonthlyData, yearlyData, timeRange]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        return `${context.dataset.label}: ₹${context.raw.toLocaleString()}`;
                    },
                    title: function (tooltipItems) {
                        if (timeRange === 'monthly') {
                            return tooltipItems[0].label;
                        }
                        return `Year: ${tooltipItems[0].label}`;
                    }
                }
            }
        },
        // scales: {
        //     x: {
        //         grid: {
        //             display: false
        //         },
        //         title: {
        //             display: true,
        //             text: timeRange === 'monthly' ? 'Month' : 'Year'
        //         }
        //     },
        //     y: {
        //         beginAtZero: true,
        //         grid: {
        //             display: false
        //         },
        //         ticks: {
        //             callback: function (value) {
        //                 return '₹' + value.toLocaleString();
        //             }
        //         },
        //         title: {
        //             display: true,
        //             text: 'Amount (₹)'
        //         }
        //     }
        // },
        scales: {
            x: {
                grid: { display: false },
                ticks: { color: '#6B7280', font: { size: 11 } }
            },
            y: {
                grid: { color: 'rgba(0,0,0,0.04)' },
                ticks: {
                    color: '#6B7280',
                    callback: v => `₹${v.toLocaleString()}`
                }
            }
        },

        interaction: {
            intersect: false,
            mode: 'index',
        },
    };
    const totalProfit = currentData?.stats?.find(s => s.label === "Total Profit")?.value || "0";
    const totalCollection = currentData?.collectionStats?.find(s => s.label === "Total Collection")?.value || "0";
    const totalExpense = currentData?.stats?.find(s => s.label === "Total Expense")?.value || "0";
    const avgExpense = currentData?.stats?.find(s => s.label === "Average Expense")?.value || "0";

    return (
        <div className="bg-white rounded-xl border border-gray-100 px-4 py-4">
            {/* bg-white rounded-lg border border-gray-100 px-5 py-4 */}
            <div className="flex justify-between items-center mb-4">
                {/* <h3 className="text-lg font-semibold text-gray-800">
                    Collection vs Expense
                </h3> */}
                <div>
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                        Financial Trend
                    </h3>
                    <p className="text-xs text-gray-400 mt-0.5">
                        Collection vs Expense · {timeRange === 'monthly' ? 'Monthly view' : 'Yearly view'}
                    </p>
                </div>
                {/* <div className="flex space-x-2">
                    <div className="flex border rounded-md overflow-hidden">
                        <button
                            className={`px-3 py-1 text-sm ${timeRange === 'monthly' ? 'bg-primary-color text-white' : 'bg-white text-gray-700'}`}
                            onClick={() => setTimeRange('monthly')}
                        >
                            Monthly
                        </button>
                        <button
                            className={`px-3 py-1 text-sm ${timeRange === 'yearly' ? 'bg-primary-color text-white' : 'bg-white text-gray-700'}`}
                            onClick={() => setTimeRange('yearly')}
                        >
                            Yearly
                        </button>
                    </div>
                </div> */}
                <div className="flex items-center bg-gray-100 rounded-full p-0.5">
                    {['monthly', 'yearly'].map((range) => (
                        <button
                            key={range}
                            onClick={() => setTimeRange(range)}
                            className={`px-3 py-1 text-xs font-medium rounded-full transition-all
                                ${timeRange === range
                                    ? 'bg-white shadow-sm text-gray-900'
                                    : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            {range === 'monthly' ? 'Monthly' : 'Yearly'}
                        </button>
                    ))}
                </div>

            </div>

            <div className="h-64">
                {currentData?.graphData?.length > 0 ? (
                    <Line data={chartData} options={options} />
                ) : (
                    <div className="h-full flex items-center justify-center text-gray-500">
                        No data available for the selected period
                    </div>
                )}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
                <StatCard
                    label="TOTAL Profit"
                    value={totalProfit}
                    color="text-green-600"
                    formatCurrency
                />
                <StatCard
                    label="TOTAL COLLECTION"
                    value={totalCollection}
                    color="text-blue-600"
                    formatCurrency
                />
                <StatCard
                    label="TOTAL EXPENSE"
                    value={totalExpense}
                    color="text-red-600"
                    formatCurrency
                />
                <StatCard
                    label="AVG EXPENSE"
                    value={avgExpense}
                    color="text-orange-600"
                    formatCurrency
                />
            </div>
        </div>
    );
};

const StatCard = ({ label, value, color, month, year, formatCurrency = false }) => {
    const formattedValue = formatCurrency
        ? parseFloat(value || 0).toLocaleString('en-IN', {
            maximumFractionDigits: 0
        })
        : value;

    const monthName = month
        ? new Date(0, month - 1).toLocaleString('default', { month: 'short' })
        : null;

    return (
        <div className="bg-gray-50 p-3 rounded-md hover:bg-gray-100 transition-colors">
            <p className="text-sm text-gray-500 font-medium uppercase truncate">{label}</p>
            <div className="flex items-baseline gap-1">
                <p className={`text-lg font-bold ${color}`}>
                    {formatCurrency ? '₹' : ''}{formattedValue}
                </p>
                {(month || year) && (
                    <span className="text-xs text-gray-500">
                        {monthName ? `in (${monthName})` : `in (${year})`}
                    </span>
                )}
            </div>
        </div>
    );
};

export default ExpenseIncomeChart;